import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';

const AUTH_SKIP_PATHS = ['/auth/login', '/auth/refresh', '/auth/logout', '/auth/client-register'];

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const router = inject(Router);
  const authService = inject(AuthService);

  const isAuthFlow = AUTH_SKIP_PATHS.some(path => req.url.includes(path));
  const accessToken = authService.getAccessToken();
  let hasRetriedAuth = false;
  let hasRetriedPermissions = false;

  const sendRequest = (tokenOverride?: string | null): Observable<HttpEvent<unknown>> => {
    const tokenToUse = tokenOverride ?? authService.getAccessToken();
    const authReq = tokenToUse
      ? req.clone({
          setHeaders: { Authorization: `Bearer ${tokenToUse}` },
        })
      : req;

    return next(authReq).pipe(
      catchError((error: HttpErrorResponse) => handleError(error))
    );
  };

  const handleError = (error: HttpErrorResponse): Observable<never | HttpEvent<unknown>> => {
    const errorCode = (error.error && (error.error.code || error.error.errorCode)) ?? null;

    if (!isAuthFlow && error.status === 401 && !hasRetriedAuth) {
      hasRetriedAuth = true;
      return from(authService.refreshAccessToken()).pipe(
        switchMap(newToken => {
          if (!newToken) {
            authService.logout({ revoke: false });
            router.navigate(['/login']);
            return throwError(() => error);
          }
          return sendRequest(newToken) as Observable<never | HttpEvent<unknown>>;
        })
      );
    }

    if (
      !isAuthFlow &&
      error.status === 403 &&
      errorCode === 'PERMISSIONS_VERSION_MISMATCH' &&
      !hasRetriedPermissions
    ) {
      hasRetriedPermissions = true;
      return from(authService.refreshAccessToken()).pipe(
        switchMap(() => from(authService.fetchAccessProfile(true))),
        switchMap(() => sendRequest(authService.getAccessToken()) as Observable<never | HttpEvent<unknown>>),
        catchError(refreshError => {
          if (refreshError instanceof HttpErrorResponse && refreshError.status === 401) {
            authService.logout({ revoke: false });
            router.navigate(['/login']);
          }
          return throwError(() => refreshError);
        })
      );
    }

    // A normal 403 is an authorization failure, not an invalid session. Keep
    // the authenticated profile and let the route/component present the error.
    if (!isAuthFlow && error.status === 401) {
      authService.logout({ revoke: false });
      router.navigate(['/login']);
    }

    return throwError(() => error);
  };

  if (accessToken && authService.isTokenExpired(accessToken) && !isAuthFlow) {
    return from(authService.refreshAccessToken()).pipe(
      switchMap(newToken => sendRequest(newToken ?? accessToken))
    );
  }

  return sendRequest(accessToken);
};
